/**
 * Where each field of the forms panel comes from, in each generation of the Rikutec process. This is the one place that
 * says it: the history reader, the identification of the process and docs/rikutec-form-fields.md all follow it.
 */
export interface FieldSource {
    process: 'current' | 'older' | 'both';
    form: string;
    key: string;
}

export interface FormField {
    field: string;
    type: 'text' | 'answer';
    /** In lookup order: the current process first. */
    sources: FieldSource[];
}

const current = (form: string, key: string): FieldSource => ({ process: 'current', form, key });
const older = (form: string, key: string): FieldSource => ({ process: 'older', form, key });
const both = (form: string, key: string): FieldSource => ({ process: 'both', form, key });

const text = (field: string, ...sources: FieldSource[]): FormField => ({ field, type: 'text', sources });
const answer = (field: string, ...sources: FieldSource[]): FormField => ({ field, type: 'answer', sources });

const contact = (prefix: string, form: string) => [
    text(`${prefix}Company`, both(form, 'company')),
    text(`${prefix}LastName`, both(form, 'last_name')),
    text(`${prefix}Address`, both(form, 'address')),
    text(`${prefix}PostalCode`, both(form, 'postal_code')),
    text(`${prefix}City`, both(form, 'city')),
    text(`${prefix}Country`, both(form, 'country')),
    text(`${prefix}Email`, both(form, 'email')),
    text(`${prefix}PhoneNumber`, both(form, 'phone_number')),
];

export const FORM_FIELDS: FormField[] = [
    text('form2Model', current('_form2', 'model'), older('_form2', 'modele')),
    text('form2NominalCapacity', current('_form2', 'nominal_capacity'), older('_form2', 'capacite_nominale')),
    text('form2SerialNumber', both('_form2', 'serial_number')),
    text(
        'form2DateInstallationOrInvoice',
        current('_form2', 'date_installation_or_invoice'),
        older('_form2', 'date_pose_ou_facture'),
    ),
    text('clientLastName', current('_form3', 'client_last_name'), older('_form3', 'last_name')),
    text('clientName', current('_form3', 'client_name'), older('_form3', 'name')),
    text('clientAddress', current('_form3', 'client_address'), older('_form3', 'address')),
    text('clientPostalCode', current('_form3', 'client_postal_code'), older('_form3', 'postal_code')),
    text('clientCity', current('_form3', 'client_city'), older('_form3', 'city')),
    text('clientCountry', current('_form3', 'client_country'), older('_form3', 'country')),
    text('clientEmail', current('_form3', 'client_email'), older('_form3', 'email')),
    text('clientPhoneNumber', current('_form3', 'client_phone_number'), older('_form3', 'phone_number')),
    ...contact('installer', '_form5'),
    ...contact('distributor', '_form6'),
    answer(
        'form8StableSoilBeddingGravel',
        current('_form8', 'stable_soil_without_groundwater_bedding_consists_of_gravel_or_small_grave'),
        older('_form8', 'bedding_consists_of_gravel_or_small_grave'),
    ),
    answer(
        'form8StableSoilSideBackfilling',
        current('_form8', 'stable_soil_without_groundwater_side_backfilling_symmetrically'),
        older('_form8', 'side_backfilling_symmetrically'),
    ),
    answer(
        'form9ImpermeableReinforcedSlab',
        current('_form9', 'impermeable_ground_or_with_the_presence_of_underground_water_reinforced_concrete_slab_connected'),
        older('_form9', 'reinforced_concrete_slab_connected'),
        older('_form9', 'laying_bed_concrete_slab'),
    ),
    answer(
        'form9ImpermeableLeanConcreteBedding',
        current('_form9', 'impermeable_ground_or_with_the_presence_of_underground_water_lean_concrete_bedding'),
        older('_form9', 'lean_concrete_bedding'),
    ),
    answer(
        'form9ImpermeableLateralBackfilling',
        current('_form9', 'impermeable_ground_or_with_the_presence_of_underground_water_lateral_backfilling'),
        older('_form9', 'lateral_backfilling'),
        older('_form9', 'side_backfilling_symmetrically'),
    ),
    answer(
        'form10UnstableLeanConcreteBedding',
        current('_form10', 'unstable_or_poorly_permeable_soil_without_groundwater_laying_bed_is_made_of_lean_concrete'),
        older('_form10', 'laying_bed_is_made_of_lean_concrete'),
    ),
    answer(
        'form10UnstableLateralBackfilling',
        current('_form10', 'unstable_or_poorly_permeable_soil_without_groundwater_lateral_backfilling'),
        older('_form10', 'lateral_backfilling'),
    ),
    // Site checks: the current process dropped these questions, and some depend on the product.
    answer('form7WaterRunoff', older('_form7', 'water_runoff')),
    answer('form7SteepSlope', older('_form7', 'event_of_a_steep_slope')),
    answer('form7DevicesInWater', older('_form7', 'devices_cannot_be_in_water')),
    answer('form7EvacuationAndVentilation', older('_form7', 'evacuation_and_ventilation')),
    answer('form7NoLoadWithin3Meters', older('_form7', 'less_that_3_meters_from_the_devices')),
    answer('form7RapidRecovery', older('_form7', 'means_taken_to_ensure_rapid_recovery')),
    answer('form7NotInFloodProneArea', older('_form7', 'device_not_located_in_flood_prone_area')),
    answer('form11WaterDecanter', older('_form11', 'water_decanter')),
    answer('form11BasketInPlace', older('_form11', 'basket_in_place_exist_site')),
    answer('form11DownstreamLiftingDevice', older('_form11', 'optional_downstream_lifting_device')),
    answer('form12BiologicalWaterReactor', older('_form12', 'biological_water_reactor')),
    answer('form12GravityFlow', older('_form12', 'gravity_flow_from_the_supply_pipe')),
    answer('form12BiologicalFilterVentilation', older('_form12', 'functional_biological_filter_ventilation')),
    answer('form13ClosedAndSecureBuffers', older('_form13', 'closed_and_secure_buffers')),
    answer('form14OwnerInformed', older('_form14', 'the_owner_is_informed_of_the_operation')),
    text('receptionQuoteNumber', both('_form15', 'quote_number')),
    text('receptionWorkInformation', both('_form15', 'work_information')),
    answer('receptionWithReserve', both('_form15', 'reception_with_reserve')),
    answer('conditionsOfUseAndMaintenanceReceived', both('_form15', 'conditions_of_use_and_maintenance_received')),
    text('receptionMadeIn', both('_form15', 'reception_made_in')),
    text('receptionDate', both('_form15', 'reception_date')),
    answer('privacyPolicyAccepted', current('_form16', 'privacy_policy_and_data_protection_accepted')),
    text('reserveKindOfReserve', both('_form1', 'kind_of_reserve')),
    text('reserveWorkToBePerformed', both('_form1', 'work_to_be_performed_with_reserve')),
    text('reserveWorkPeriod', both('_form1', 'work_with_reserve_will_make_in_a_period_of')),
    text('reserveMadeIn', both('_form1', 'work_with_reserve_made_in')),
    text('reserveDate', both('_form1', 'work_with_reserve_date')),
];

/** The site entered for the installation. Read as one address, see installationFromHistory. */
export const SITE_FIELDS: FormField[] = [
    text('installationAddress', current('_form3', 'installation_address'), older('_form4', 'address')),
    text('installationPostalCode', current('_form3', 'installation_postal_code'), older('_form4', 'postal_code')),
    text('installationCity', current('_form3', 'installation_city'), older('_form4', 'city')),
    text('installationCountry', current('_form3', 'installation_country'), older('_form4', 'country')),
];

export const SOIL_SELECTOR = 'condition["_form8","_form9", "_form10"]';
export const SOIL_FORMS = ['_form8', '_form9', '_form10'];

// The older pose node also has a _form1, of headers only: these keys are what tells the reserves apart.
const RESERVE_KEYS = ['kind_of_reserve', 'work_with_reserve_made_in', 'work_with_reserve_date'];

export const formsOf = (note: any) => {
    const forms = note?.data?.customFields?.forms;
    return forms && typeof forms === 'object' ? forms : {};
};

export const holdsForm = (notes: any[], form: string) =>
    notes.some((note) => {
        const held = formsOf(note)[form];
        if (form === '_form1') {
            return RESERVE_KEYS.some((key) => held?.[key]);
        }
        return !!held && Object.keys(held).length > 0;
    });
